/**
 * Setup script for Targeted Trending Videos Firestore collection
 * These are specific trending video examples that users can mimic
 * (Different from the generic "trending" AI style option)
 * 
 * Run this once to populate the trendingVideos/current document with sample data
 * 
 * To run: Import and call setupTrendingVideos() from your app
 */

import { db } from './firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

export const setupTrendingVideos = async () => {
  try {
    const currentWeekData = {
      weekOf: "2024-10-20",
      weekNumber: 42,
      isActive: true,
      uploadedAt: serverTimestamp(),
      uploadedBy: "admin",
      
      videos: [
        {
          id: "video_1",
          title: "POV Unboxing Style",
          description: "First-person unboxing with authentic reactions and excitement",
          
          // Using placeholder video - replace with actual trending video URL
          videoUrl: "/header-video-1v2.mp4",
          thumbnailUrl: null,
          
          // AI-generated style prompt for script generation
          stylePrompt: `Create a POV (Point of View) unboxing style script with these characteristics:

VISUAL STYLE:
- First-person camera perspective throughout
- Close-up shots of hands opening packaging
- Quick cuts between product angles (3-5 seconds each)
- Focus on texture, packaging details, and product features
- Authentic, in-the-moment filming style

TONE & DELIVERY:
- Excited and authentic reactions
- Conversational, stream-of-consciousness dialogue
- Natural pauses and "umm" moments for authenticity
- Genuine surprise and discovery as you unbox

HOOK PATTERN:
- Start with casual, relatable opening like:
  * "Wait, this just came in and I'm obsessed"
  * "POV: Your new favorite thing just arrived"
  * "Okay so this packaging alone..."
  * "You guys asked for an unboxing so here it is"

BODY CONTENT:
- Commentary on packaging quality and presentation
- Verbal reactions as each layer is revealed
- Touch and texture descriptions ("Oh this feels so soft")
- First impressions and feature highlights
- Personal observations and excited commentary

CALL-TO-ACTION:
- Soft, natural CTA that doesn't break the authentic vibe
- Examples:
  * "Link in bio if you want to try it"
  * "I'll drop the link in my stories"
  * "Comment 'LINK' and I'll send it to you"

PACING:
- Fast-paced with quick scene changes
- Keep energy high throughout
- Build anticipation with each reveal
- End on a high note with final product showcase

Remember: This should feel like a friend showing you their new purchase, not a commercial. Keep it genuine, excited, and conversational.`,
          
          category: "Product Review",
          platform: "TikTok",
          duration: 45
        },
        
        {
          id: "video_2",
          title: "Get Ready With Me Review",
          description: "Morning routine with natural product integration and storytelling",
          
          videoUrl: "/header-video-2v2.mp4",
          thumbnailUrl: null,
          
          stylePrompt: `Create a Get Ready With Me (GRWM) style script with these characteristics:

VISUAL STYLE:
- Mirror/bathroom setup or well-lit personal space
- Multiple angle switches between close-ups and full-face shots
- Real-time routine being performed on camera
- Natural lighting, casual home environment
- Show product application and usage in real-time

TONE & DELIVERY:
- Relaxed, chatty, and conversational
- Like talking to a friend while getting ready
- Personal stories and anecdotes woven throughout
- Calm but engaging energy
- Authentic, relatable personality

HOOK PATTERN:
- Start with situational opening like:
  * "Get ready with me while I tell you about..."
  * "Doing my morning routine and testing this..."
  * "GRWM and let's talk about why I'm obsessed"
  * "Getting ready for [event] using..."

BODY CONTENT:
- Narrate your actual routine as you do it
- Naturally introduce product as part of routine
- Share personal story or context while applying
- Explain why you chose this product
- Show application technique and results
- Mix routine steps with product talk
- Include before/during/after moments

STORYTELLING ELEMENTS:
- Personal anecdote related to the product need
- Problem you had that this solves
- How you discovered or chose the product
- Real results you've experienced
- Relatable moments and situations

CALL-TO-ACTION:
- Integrated naturally at the end
- Examples:
  * "I'll link everything I used today"
  * "Check my bio for the products"
  * "Let me know if you want a full review"

PACING:
- Moderate pace, not rushed
- Allow moments of routine to breathe
- Balance action with talking
- Natural rhythm of actual getting-ready process

Remember: This should feel like hanging out with a friend while they get ready. The product is naturally part of the routine, not forced or salesy. Keep it real, relatable, and conversational.`,
          
          category: "Lifestyle",
          platform: "TikTok",
          duration: 60
        },
        
        {
          id: "video_3",
          title: "Before & After Transformation",
          description: "Dramatic results showcase with problem-solution narrative",
          
          videoUrl: "/header-video-3v2.mp4",
          thumbnailUrl: null,
          
          stylePrompt: `Create a Before & After Transformation style script with these characteristics:

VISUAL STYLE:
- Clear split-screen or side-by-side comparison
- Strong contrast between before and after states
- Close-up shots emphasizing the transformation
- Dramatic reveal moment
- Visual proof of results

TONE & DELIVERY:
- Confident and results-focused
- Honest about the problem/struggle
- Excited about the solution and results
- Relatable frustration turning into satisfaction
- Empowering and encouraging

HOOK PATTERN:
- Start with the problem or dramatic statement:
  * "I struggled with [problem] until I found this"
  * "Before vs After using [product]"
  * "This actually changed everything for me"
  * "POV: You finally found something that works"
  * "Watch this transformation"

STRUCTURE:
1. PROBLEM SETUP (First 5-8 seconds):
   - Establish the issue you were facing
   - Show or describe the "before" state
   - Make it relatable and specific
   - Build empathy with viewers who have same issue

2. SOLUTION INTRODUCTION (8-15 seconds):
   - Introduce the product naturally
   - Explain why you tried it
   - Set up what it promised to do
   - Brief mention of how you used it

3. TRANSFORMATION REVEAL (15-25 seconds):
   - Show the dramatic before/after comparison
   - Point out specific improvements
   - Express genuine excitement about results
   - Provide timeframe (how long it took)
   - Include key details about the experience

4. RESULTS & BENEFITS (25-40 seconds):
   - Highlight specific changes and improvements
   - Share unexpected positive outcomes
   - Be specific about what's different
   - Include any tips for best results

CALL-TO-ACTION:
- Results-driven CTA:
  * "If you want to see results like this..."
  * "Link in bio to try it yourself"
  * "This is the one that actually worked"

KEY ELEMENTS:
- Honesty about the struggle
- Specific, measurable results
- Visual proof whenever possible
- Realistic timeframe and expectations
- Genuine enthusiasm about transformation

PACING:
- Quick hook with the problem
- Build momentum through solution intro
- Peak excitement at the reveal
- Sustain energy through results discussion
- Strong close with confident recommendation

Remember: This is about showing real transformation and giving hope to others with the same problem. Be specific, be honest, and let the results speak for themselves. The drama is in the contrast, not in overselling.`,
          
          category: "Transformation",
          platform: "TikTok",
          duration: 40
        }
      ]
    };

    // Create/update the current trending videos document
    await setDoc(doc(db, 'trendingVideos', 'current'), currentWeekData);
    
    console.log('✅ Targeted trending videos collection created successfully!');
    console.log('📹 Added 3 sample videos with AI-generated style prompts');
    return { success: true, message: 'Targeted trending videos setup complete' };
    
  } catch (error) {
    console.error('❌ Error setting up trending videos:', error);
    throw error;
  }
};

// Helper function to get current week's targeted trending videos
export const getCurrentTrendingVideos = async () => {
  try {
    const { getDoc } = await import('firebase/firestore');
    const docRef = doc(db, 'trendingVideos', 'current');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log('No targeted trending videos found');
      return null;
    }
  } catch (error) {
    console.error('Error fetching targeted trending videos:', error);
    throw error;
  }
};

// Helper function to check if targeted trending videos exist
export const checkTrendingVideosExist = async () => {
  try {
    const data = await getCurrentTrendingVideos();
    return data !== null && data.videos && data.videos.length > 0;
  } catch (error) {
    return false;
  }
};

